import React, { useState, useEffect } from 'react';
import CurrencyFlag from 'react-currency-flags';

// Define the structure of the API response
interface ExchangeRates {
  date: string;
  usd: {
    [key: string]: number;
  };
}

const Ticker: React.FC = () => {
  const [rates, setRates] = useState<Record<string, number> | null>(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await fetch('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json');
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data: ExchangeRates = await response.json();
        // We only want a few key currencies for the ticker
        const selectedRates = {
          'EUR': data.usd.eur,
          'GBP': data.usd.gbp,
          'JPY': data.usd.jpy,
          'ZAR': data.usd.zar,
          'AUD': data.usd.aud,
          'CAD': data.usd.cad,
          'CNY': data.usd.cny,
        };
        setRates(selectedRates);
      } catch (error) {
        console.error("Failed to fetch currency rates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRates();

    const handleScroll = () => {
      setIsVisible(window.scrollY < 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (loading) {
    return (
      <div className="bg-white/5 backdrop-blur-sm border-b border-white/10 py-2.5 w-full">
      <div className="w-full">
        <div className="relative">
          <div className="whitespace-nowrap">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
              <div key={i} className="h-10 w-32 bg-navy/30 rounded-full inline-block mx-2"></div>
            ))}
          </div>
        </div>
      </div>
    </div>
    );
  }

  if (!rates) {
    return (
      <div className="bg-white/90 backdrop-blur-sm shadow-sm border-b border-gray-100 py-2.5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-600">Live currency rates currently unavailable</p>
        </div>
      </div>
    );
  }

  const tickerItems = Object.entries(rates).map(([currency, rate]) => (
    <div key={currency} className="inline-flex items-center mx-4 group">
      <div className="relative">
          <div className="flex items-center space-x-3 bg-navy hover:bg-navy-dark text-white px-4 py-2 rounded-full transition-all duration-200 hover:shadow-lg hover:scale-105 border border-navy-light/20">
          <div className="w-6 h-6 rounded-full overflow-hidden border-2 border-white/30 flex-shrink-0 shadow-sm relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full h-full flex items-center justify-center">
                <CurrencyFlag 
                  currency={currency}
                  size="sm"
                  className="min-w-full min-h-full object-cover"
                />
              </div>
            </div>
          </div>
          <span className="font-bold text-sm tracking-wide">{currency}</span>
          <span className="text-white/80 mx-1">/</span>
          <span className="font-medium text-sm">USD</span>
          <span className="ml-1 font-bold text-sm">{rate.toFixed(4)}</span>
        </div>
      </div>
    </div>
  ));

  return (
    <div className={`transition-all duration-300 ease-in-out ${isVisible ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
      <div className="bg-white/5 backdrop-blur-sm border-b border-white/10 py-2.5 w-full overflow-hidden">
      <div className="w-full">
        <div className="relative overflow-hidden">
          <div className="whitespace-nowrap inline-block animate-marquee w-max">
            {tickerItems}
            {tickerItems} {/* Duplicate for seamless looping */}
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

const CurrencyTicker: React.FC = () => {
  return <Ticker />;
};

export default CurrencyTicker;